import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OpList } from 'new-components/DLS/OpList/OpList';
import { heliumReceive, heliumSend } from 'utils/helpers';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { OnSubmitArgs, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { getOpParams } from 'utils/getOpParams';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';

import { toLocalByTimezone } from '../../../../utils/dates';
import { VisitsState } from '../../../../store/slices/visitsSlice';
import { Note } from '../../../../types/visitTypes';
import { DATE_TIME_AM_PM_FORMAT } from '../../../../constants/dates';
import { ImmutableRootState } from '../../../../store/store';

interface NotesTabProps {
  isActive: boolean;
}

interface VisitorLogResponse {
  data?: Note[] | null;
}

interface NotesFormValues {
  note: string;
}

const NotesTab = ({ isActive }: NotesTabProps) => {
  const { t } = useTranslation();

  const { orgId } = getOpParams();
  const { userId: globalUserId } = useIdentityAndUser();
  const { isValid: hasVisitorlogWrite } = useValidateScopes([
    'o{orgId}-visitorVisitorLog:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];

  const [visitNotes, setVisitNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [form] = OpForm.useForm();

  // Fetch visit notes
  const fetchVisitNotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = (await heliumReceive('getVisitorLog', [
        orgId,
        selectedVisit?.id,
      ])) as VisitorLogResponse;
      setVisitNotes(Array.isArray(response.data) ? response.data : []);
    } catch {
      // Failed to fetch visit notes
    } finally {
      setLoading(false);
    }
  }, [orgId, selectedVisit?.id]);

  useEffect(() => {
    if (isActive) {
      fetchVisitNotes();
    }
  }, [isActive, fetchVisitNotes]);

  const handleSubmit = useCallback(
    async ({ values }: OnSubmitArgs<NotesFormValues>) => {
      const addVisitNote = async () => {
        if (!globalUserId) {
          notificationService.show(
            'error',
            t('notification.featureMasterModeUnavailable'),
          );
          return;
        }

        setLoading(true);
        try {
          await heliumSend('insertVisitorLog', [orgId, selectedVisit?.id], {
            logUserId: globalUserId,
            log: values.note,
          });
          const response = (await heliumReceive('getVisitorLog', [
            orgId,
            selectedVisit?.id,
          ])) as VisitorLogResponse;
          setVisitNotes(Array.isArray(response.data) ? response.data : []);
          form.resetFields();
          notificationService.show('success', t('visitors.notes.success'));
        } catch {
          notificationService.show('error', t('visitors.notes.fail'));
        } finally {
          setLoading(false);
        }
      };
      addVisitNote();
    },
    [globalUserId, orgId, form, selectedVisit?.id, t],
  );

  return (
    <div>
      {hasVisitorlogWrite && (
        <OpForm
          form={form}
          onSubmit={handleSubmit}
          hasError={false}
          defaultButtons={false}
        >
          <OpForm.TextAreaInput
            name="note"
            rules={[
              { required: true, message: t('visitors.notes.notesRequired') },
            ]}
            rows={4}
            placeholder={t('visitors.notes.placeholder')}
          />
          <OpForm.Button type="primary" htmlType="submit">
            {t('visitors.notes.save')}
          </OpForm.Button>
        </OpForm>
      )}
      <OpList
        dataSource={visitNotes}
        loading={loading}
        renderItem={(item) => {
          const note = item as Note;
          return (
            <OpList.Item
              key={note.id}
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
              }}
            >
              <OpList.Item.Meta
                description={toLocalByTimezone(
                  note.timestamp,
                  selectedVisit?.location?.timezone,
                )?.format(DATE_TIME_AM_PM_FORMAT)}
              />
              {note.log}
            </OpList.Item>
          );
        }}
      />
    </div>
  );
};

export default NotesTab;
